import React, { useEffect, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  InputAdornment,
} from "@mui/material";
import { FaMapMarkerAlt } from "react-icons/fa";
import { useTheme } from "@mui/material/styles";
import tzlookup from "tz-lookup";
import "./KitchenLocation.css";

const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const useLoadGoogleMapsScript = (callback) => {
  useEffect(() => {
    if (!apiKey) {
      console.error("Google Maps API key is not defined.");
      return;
    }

    if (!document.querySelector(`[src*="maps.googleapis.com"]`)) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        const waitForPlaces = () => {
          if (
            window.google &&
            window.google.maps &&
            window.google.maps.places
          ) {
            callback();
          } else {
            setTimeout(waitForPlaces, 100);
          }
        };
        waitForPlaces();
      };
      script.onerror = () => console.error("Error loading Google Maps script.");
      document.head.appendChild(script);
    } else {
      const waitForPlaces = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          callback();
        } else {
          setTimeout(waitForPlaces, 100);
        }
      };
      waitForPlaces();
    }
  }, [callback]);
};

const KitchenLocation = ({ editMode, kitchenData, updateKitchenData }) => {
  const addressInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const defaultCoordinatesByTimeZone = {
    "America/Los_Angeles": { lat: 34.0522, lng: -118.2437 },
    "America/New_York": { lat: 40.7128, lng: -74.006 },
    "America/Chicago": { lat: 41.8781, lng: -87.6298 },
  };
  const defaultCoordinates = defaultCoordinatesByTimeZone[userTimeZone] || {
    lat: 34.0522,
    lng: -118.2437,
  };

  useEffect(() => {
    if (kitchenData.latitude == null || kitchenData.longitude == null) {
      updateKitchenData({
        latitude: defaultCoordinates.lat,
        longitude: defaultCoordinates.lng,
      });
    }
  }, [kitchenData.latitude, kitchenData.longitude]);

  useLoadGoogleMapsScript(() => {
    if (
      window.google &&
      window.google.maps &&
      window.google.maps.places &&
      addressInputRef.current
    ) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        { types: ["geocode"], componentRestrictions: { country: "us" } }
      );
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place && place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          updateKitchenData({
            address: place.formatted_address,
            latitude: lat,
            longitude: lng,
          });
        }
      });
    }
  });

  useEffect(() => {
    if (kitchenData.latitude && kitchenData.longitude) {
      try {
        const tz = tzlookup(kitchenData.latitude, kitchenData.longitude);
        if (kitchenData.timeZone !== tz) {
          updateKitchenData({ timeZone: tz });
        }
      } catch (err) {
        console.error("Error determining timezone:", err);
      }
    }
  }, [kitchenData.latitude, kitchenData.longitude]);

  return (
    <Box
      sx={{
        p: isMobile ? 1 : 4,
        background: "#fff",
      }}
    >
      <Typography variant="h6" gutterBottom>
        Kitchen Location
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            placeholder="Enter kitchen address"
            inputRef={addressInputRef}
            value={kitchenData.address || ""}
            onChange={(e) => updateKitchenData({ address: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaMapMarkerAlt style={{ color: "#888" }} />
                </InputAdornment>
              ),
            }}
            helperText="Note: The exact location will be shared only upon booking confirmation."
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default KitchenLocation;
